import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import {
  ClickAnalyticsPlugin,
  IClickAnalyticsConfiguration,
} from '@microsoft/applicationinsights-clickanalytics-js';
import { ApplicationInsights, ICustomProperties, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { isEmpty, pickBy } from 'lodash-es';
import { BehaviorSubject, Observable, ReplaySubject, catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { getIsNotNullable, getIsNullable } from 'src/app/core/nullable-utils';
import { IxRestApi } from 'src/app/core/services/api/ix-rest-api';
import { RemoteConfigurationError, getConfigurationValue$ } from '../../../core/services/api-fns';
import { CustomProperties, ThirdPartyTrackingService } from '../tracking.interface';

@Injectable({ providedIn: 'root' })
export class InsightsService implements ThirdPartyTrackingService {
  readonly #ixRestApi = inject(IxRestApi);
  readonly #router = inject(Router);

  #appInsights: ApplicationInsights | undefined;
  readonly #isOperational$ = new ReplaySubject<boolean>(1);

  readonly #userProperties$ = new BehaviorSubject<ICustomProperties>({});
  readonly #profileProperties$ = new BehaviorSubject<ICustomProperties>({});

  setProfileProperties(properties: ICustomProperties): void {
    const nextProps = { ...this.#profileProperties$.getValue(), ...properties };
    this.#profileProperties$.next(pickBy(nextProps, getIsNotNullable));
  }

  setUserProperties(properties: ICustomProperties): void {
    const nextProps = { ...this.#userProperties$.getValue(), ...properties };
    this.#userProperties$.next(pickBy(nextProps, getIsNotNullable));
  }

  setIdentifiedUserContext(userId: string): void {
    this.#appInsights?.setAuthenticatedUserContext(userId, undefined, true);
  }

  async trackEvent(name: string, properties?: CustomProperties): Promise<void> {
    if (await firstValueFrom(this.#isOperational$.asObservable())) {
      return this.#appInsights?.trackEvent({ name }, { ...this.#userProperties$.getValue(), ...properties });
    }
  }

  init(): Observable<boolean> {
    return getConfigurationValue$(this.#ixRestApi, 'appinsights').pipe(
      map(key => {
        if (getIsNullable(key) || isEmpty(key)) {
          throw new RemoteConfigurationError('AppInsights configuration key not set');
        }
        return key;
      }),
      map(key => createAppInsightsInstance(key, this.#router)),
      map(appInsights => {
        this.#appInsights = appInsights;
        this.#appInsights.addTelemetryInitializer(filteringFunction);
        this.#appInsights.addTelemetryInitializer(env => addUserProps(env, this.#userProperties$.getValue()));
        this.#appInsights.loadAppInsights();
        return true;
      }),
      catchError(error => {
        console.warn('Failed to initialize AppInsights. Tracking disabled.', error);
        return of(false);
      }),
      tap(val => this.#isOperational$.next(val)),
    );
  }
}

function createAppInsightsInstance(connectionString: string, router: Router) {
  const angularPlugin = new AngularPlugin();
  const clickPluginInstance = new ClickAnalyticsPlugin();

  // https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-feature-extensions?tabs=javascriptwebsdkloaderscript#add-advanced-configuration
  const clickPluginConfig: IClickAnalyticsConfiguration = {
    autoCapture: true,
    dataTags: { useDefaultContentNameOrId: true },
  };

  return new ApplicationInsights({
    config: {
      connectionString,
      extensions: [angularPlugin, clickPluginInstance],
      extensionConfig: {
        [angularPlugin.identifier]: {
          router,
          errorServices: [new ErrorHandler()],
        },
        [clickPluginInstance.identifier]: clickPluginConfig,
      },
    },
  });
}

const trackingIgnoreList: string[] = [
  'azconfig.io/kv', // feature toggle config
];
function filteringFunction(envelope: ITelemetryItem): boolean | void {
  return !trackingIgnoreList.some(denied => envelope.baseData?.name.includes(denied));
}

function addUserProps(envelope: ITelemetryItem, props: ICustomProperties): boolean | void {
  if (envelope.baseData) {
    envelope.baseData.properties = { ...envelope.baseData.properties, ...props };
  }
}
